import React, { useState, useEffect } from 'react';
import { logSessionInfo } from '../../../../../services/logger';
import { getCopyByGradeband } from '../../../../../services/ThemingService';
import { COPY_STRINGS } from '../../../../../constants/translatedCopy';
import './RetestMicrophoneView.scss';

const HELP_ARTICLE_URL = 'https://help.amiralearning.com/s/article/How-To-Check-And-Adjust-Audio-Microphone-Settings';

/**
 * RetestMicrophoneView component displays instructions for retesting the microphone
 * after an initial test didn't sound right. Provides troubleshooting tips and a link
 * to the help article.
 * 
 * @param {Object} props - Component props.
 * @param {Function} props.goToLocalRecording - Callback to navigate to local recording view.
 * @param {Object} props.speechProps - Speech props for Amira TTS.
 */
export default function RetestMicrophoneView(props) {
  const { goToLocalRecording, speechProps } = props;
  const [isLoading, setIsLoading] = useState(false);

  const studentChecksCopy = getCopyByGradeband(COPY_STRINGS, 'student_checks');

  /**
   * Handles the "Test Now" button click.
   * Disables the button, plays the speech prompt, then navigates to recording.
   */
  const handleTestNow = async () => {
    if (isLoading) return;

    setIsLoading(true);
    logSessionInfo('teacherTroubleshoot_TEACHER_AUDIO_RETEST_STARTED', {});

    try {
      // Play the speech prompt: "Go ahead and say -- It's time to read now!"
      await speechProps.speakWithPromise({ 
        textToSpeak: studentChecksCopy.student_asr_initial_prompt 
      });
      
      // Navigate to local recording view after speech completes
      goToLocalRecording();
    } catch (error) {
      console.error('RetestMicrophoneView: Error during speech prompt', error);
      // Reset loading state and still navigate to recording even if speech fails
      setIsLoading(false);
      goToLocalRecording();
    }
  };

  return (
    <div className="retest-microphone-view">
      <div className="retest-microphone-view__content">
        <div className="retest-microphone-view__header">
          <svg 
            className="retest-microphone-view__header-icon"
            xmlns="http://www.w3.org/2000/svg" 
            width="75" 
            height="75" 
            viewBox="0 0 36 53" 
            fill="none"
          >
            <path fillRule="evenodd" clipRule="evenodd" d="M18 37.2305C23.6726 37.2305 28.2857 32.5308 28.2857 26.7577H22.3636C21.4599 26.7577 20.7273 26.0251 20.7273 25.1214C20.7273 24.2176 21.4599 23.485 22.3636 23.485H28.2857V20.2123H22.3636C21.4599 20.2123 20.7273 19.4797 20.7273 18.5759C20.7273 17.6722 21.4599 16.9396 22.3636 16.9396H28.2857V13.6668H22.3636C21.4599 13.6668 20.7273 12.9342 20.7273 12.0305C20.7273 11.1267 21.4599 10.3941 22.3636 10.3941H28.2659C27.9331 4.92223 23.4567 0.575928 18 0.575928C12.5433 0.575928 8.06695 4.92223 7.73414 10.3941H13.6364C14.5401 10.3941 15.2727 11.1267 15.2727 12.0305C15.2727 12.9342 14.5401 13.6668 13.6364 13.6668H7.71429V16.9396H13.6364C14.5401 16.9396 15.2727 17.6722 15.2727 18.5759C15.2727 19.4797 14.5401 20.2123 13.6364 20.2123H7.71429V23.485H13.6364C14.5401 23.485 15.2727 24.2176 15.2727 25.1214C15.2727 26.0251 14.5401 26.7577 13.6364 26.7577H7.71429C7.71429 32.5308 12.3274 37.2305 18 37.2305ZM36 26.7577V21.5214C36 20.827 35.7291 20.1611 35.2468 19.67C34.7646 19.179 34.1106 18.9032 33.4286 18.9032C32.7466 18.9032 32.0925 19.179 31.6103 19.67C31.1281 20.1611 30.8571 20.827 30.8571 21.5214V26.7577C30.8571 33.9761 25.0894 39.8487 18 39.8487C10.9106 39.8487 5.14286 33.9761 5.14286 26.7577V21.5214C5.14286 20.827 4.87194 20.1611 4.3897 19.67C3.90747 19.179 3.25341 18.9032 2.57143 18.9032C1.88944 18.9032 1.23539 19.179 0.753155 19.67C0.270918 20.1611 0 20.827 0 21.5214V26.7577C0 35.9737 6.71914 43.5979 15.4286 44.8756V47.7032H7.71429C7.0323 47.7032 6.37825 47.979 5.89601 48.47C5.41378 48.961 5.14286 49.627 5.14286 50.3214C5.14286 51.0158 5.41378 51.6817 5.89601 52.1727C6.37825 52.6637 7.0323 52.9396 7.71429 52.9396H28.2857C28.9677 52.9396 29.6218 52.6637 30.104 52.1727C30.5862 51.6817 30.8571 51.0158 30.8571 50.3214C30.8571 49.627 30.5862 48.961 30.104 48.47C29.6218 47.979 28.9677 47.7032 28.2857 47.7032H20.5714V44.8756C29.2809 43.5979 36 35.9737 36 26.7577Z" fill="#3E83BE"/>
          </svg>
          <div className="retest-microphone-view__title">
            Common Microphone Problems
          </div>
        </div>
        
        <div className="retest-microphone-view__instruction">
          <div className="retest-microphone-view__instruction-title">
            Here are some suggestions:
          </div>
          <ul className="retest-microphone-view__instruction-list">
            <li>Make sure the correct microphone is selected in your browser.</li>
            <li>Try switching to your device's internal microphone.</li>
            <li>Try switching to a different headset or microphone.</li>
            <li>Try switching to a different computer, device, etc.</li>
          </ul>
        </div>

        <div className="retest-microphone-view__help-link-container">
          Visit this{' '}
          <a 
            href={HELP_ARTICLE_URL}
            target="_blank" 
            rel="noopener noreferrer"
            className="retest-microphone-view__help-link"
          >
            help link
          </a> for more troubleshooting tips.
        </div>
      </div>
      
      <div className="retest-microphone-view__button-container">
        <button 
          className={`retest-microphone-view__button ${isLoading ? 'disabled' : ''}`}
          onClick={handleTestNow}
          disabled={isLoading}
        >
          {isLoading ? 'Please wait...' : 'Retest Now'}
        </button>
      </div>
    </div>
  );
}
